import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase.js';
import { safeInsert, safeUpdate, safeUpsert, safeDelete, flushQueue, getSessionPendingCount } from '../lib/offline.js';
import { isProduitCher } from '../lib/produits-chers.js';
import { getProductFlags, flagBadges, hasAnyFlag } from '../lib/product-flags.js';
import { callAction } from '../lib/odoo.js';
import { getSignedUrl, extractPathFromUrl, compressImage } from '../lib/photos.js';
import QrScanner from './QrScanner.jsx';
import { syncSessionToAsana, uploadAttachment as uploadAsanaAttachment, addComment as addAsanaComment } from '../lib/asana.js';
import { playBeep } from '../lib/audio.js';
import { DEMO_PRODUCTS } from '../lib/demo.js';
import PrepHeader from './PrepHeader.jsx';
import PrepProduitsTab from './PrepProduitsTab.jsx';
import PrepCaissesSection from './PrepCaissesSection.jsx';
import PrepPhotosSection from './PrepPhotosSection.jsx';
import PrepAnomaliesSection from './PrepAnomaliesSection.jsx';
import PrepValidation from './PrepValidation.jsx';
import PrepAReprendre, { STATUTS_EN_COURS as A_REPRENDRE_EN_COURS } from './PrepAReprendre.jsx';
import { journaliser, ACTIONS, estPartie } from '../lib/journal.js';
import { attribuerNumero } from '../lib/caisse-numero.js';

// controleAvantPrete (facultatif, Prépa V2 uniquement) : vérification
// supplémentaire avant « Prête ». Non fourni par Prep : aucun changement.
export default function PrepDetail({ session, user, reliquats = [], onBack, onSessionUpdate, initialStep = null, controleAvantPrete = null }) {
  const [products, setProducts] = useState([]);
  const [regFlags, setRegFlags] = useState({}); // { [product_id]: { stup, cyto } }
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openStep, setOpenStep] = useState(initialStep);
  const [productsOpen, setProductsOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [newCaisse, setNewCaisse] = useState('');
  const [newCaisseType, setNewCaisseType] = useState(null);
  const [toast, setToast] = useState(null);
  const scanRef = useRef(null);
  const anomScanRef = useRef(null);
  const dejaDefile = useRef(false);   // défilement vers les anomalies : une seule fois
  const [anomalies, setAnomalies] = useState([]);
  const [versionAReprendre, setVersionAReprendre] = useState(0);
  const [idsDeCote, setIdsDeCote] = useState([]);
  // Nouveau parcours « à reprendre » seulement si sa table existe (SQL 035) ;
  // sinon l'ancien changement d'emplacement immédiat reste en place.
  const [aReprendreDispo, setAReprendreDispo] = useState(false);
  const [assistant, setAssistant] = useState(null);   // { product } nouveau signalement · { itemId } reprise
  useEffect(() => {
    supabase.from('elixir_prep_a_reprendre').select('id', { head: true, count: 'exact' }).limit(1)
      .then(({ error }) => setAReprendreDispo(!error), () => setAReprendreDispo(false));
  }, []);
  const [remarks, setRemarks] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pendingAnomaly, setPendingAnomaly] = useState(null);
  const [anomFilter, setAnomFilter] = useState('');
  const [freeRemark, setFreeRemark] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [nbCartons, setNbCartons] = useState((session.nb_cartons || 0) + (session.nb_cartons_vrac || 0));
  // Caisses deja ouvertes pour CETTE pharmacie dans d autres sessions non cloturees
  const [otherCaisses, setOtherCaisses] = useState([]);
  // Caisses encore chez la pharmacie depuis une livraison precedente (a recuperer)

  // ═══════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════

  useEffect(() => {
    const isDemo = String(session.id).startsWith('demo-');
    if (isDemo) {
      setProducts(DEMO_PRODUCTS[session.id] || []);
      setCaisses([]); setAnomalies([]); setRemarks([]); setPhotos([]);
      setLoading(false);
      return;
    }
    loadAll();
  }, [session.id]);

  // Recharger les produits quand la session est mise à jour par la synchro
  // (updated_at change via Supabase Realtime → le parent passe un nouveau session prop)
  const lastUpdatedRef = React.useRef(session.updated_at);
  useEffect(() => {
    if (String(session.id).startsWith('demo-')) return;
    if (session.updated_at && session.updated_at !== lastUpdatedRef.current) {
      lastUpdatedRef.current = session.updated_at;
      console.log('[realtime] Session mise à jour, rechargement produits...');
      loadProducts();
    }
  }, [session.updated_at]);

  // Realtime sur les données annexes de la session (multi-utilisateur) :
  // caisses, photos, anomalies, remarques. Les compteurs nb_amb/nb_froid
  // sont recalculés en base par trigger (sql/007) — ici on ne fait que
  // rafraîchir les listes affichées.
  useEffect(() => {
    if (String(session.id).startsWith('demo-')) return;
    const filter = 'session_id=eq.' + session.id;
    const channel = supabase
      .channel('prep-side-' + session.id)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'elixir_prep_caisses', filter }, () => loadCaisses())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'elixir_prep_photos', filter }, () => loadPhotos())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'elixir_prep_anomalies', filter }, () => loadAnomalies())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'elixir_prep_remarks', filter }, () => loadRemarks())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [session.id]);

  // Écouter aussi les changements sur les lignes produits de cette session
  useEffect(() => {
    if (String(session.id).startsWith('demo-')) return;
    const channel = supabase
      .channel('order-lines-' + session.id)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'elixir_prep_order_lines',
        filter: 'session_id=eq.' + session.id,
      }, () => {
        console.log('[realtime] Lignes produits modifiées, rechargement...');
        loadProducts();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [session.id]);

  const isClaimed = !!session.prepared_by;
  const isMyPrep = session.prepared_by === user?.id;
  const claimedByOther = isClaimed && !isMyPrep;

  async function claimPrep() {
    if (String(session.id).startsWith('demo-')) {
      if (onSessionUpdate) onSessionUpdate({ ...session, prepared_by: user.id, prepared_by_name: user.name, status: 'en_cours', has_new_orders: false });
      return;
    }

    // 1. Vérifier que la session n'est pas déjà prise par quelqu'un d'autre
    const { data: current } = await supabase.from('elixir_prep_sessions')
      .select('prepared_by, prepared_by_name').eq('id', session.id).single();
    if (current && current.prepared_by && current.prepared_by !== user.id) {
      alert('Déjà pris par ' + (current.prepared_by_name || 'un autre magasinier'));
      return;
    }

    // 2. Vérifier si j'ai déjà une prépa en cours
    const { data: myActive } = await supabase.from('elixir_prep_sessions')
      .select('id, partner_name')
      .eq('prepared_by', user.id)
      .eq('status', 'en_cours')
      .neq('id', session.id)
      .limit(1);
    if (myActive && myActive.length > 0) {
      alert('Tu as déjà une préparation en cours : ' + myActive[0].partner_name + '\nValide-la en "Prête" ou libère-la avant d\'en prendre une autre.');
      return;
    }

    // 3. Write — atomic: only if still unclaimed
    const { error, count } = await supabase.from('elixir_prep_sessions').update({
      prepared_by: user.id, prepared_by_name: user.name,
      status: 'en_cours', has_new_orders: false, updated_at: new Date().toISOString(),
      claimed_at: new Date().toISOString(),   // heure de prise en main : sert à libérer après 1 h sans activité
    }).eq('id', session.id).or('prepared_by.is.null,prepared_by.eq.' + user.id);
    if (error) { console.error('claimPrep error:', error); return; }

    // 4. Recheck anti-race — vérifier que c'est bien moi après le write
    const { data: check } = await supabase.from('elixir_prep_sessions')
      .select('prepared_by').eq('id', session.id).single();
    if (check && check.prepared_by !== user.id) {
      alert('Déjà pris par un autre magasinier');
      return;
    }

    // Trace métier avec la pharmacie : le journal technique ne sait pas
    // laquelle a été prise.
    journaliser(ACTIONS.PRISE, { session, user });
    if (onSessionUpdate) onSessionUpdate({ ...session, prepared_by: user.id, prepared_by_name: user.name, status: 'en_cours', has_new_orders: false });
  }

  async function releasePrep() {
    const totalColis = nbAmb + nbFroid + nbCartons;
    const hasWork = totalColis > 0 || photos.length > 0;

    if (String(session.id).startsWith('demo-')) {
      if (onSessionUpdate) onSessionUpdate({ ...session, prepared_by: null, status: 'en_cours' });
      return;
    }

    if (hasWork) {
      // La question était inversée : « Annuler » effaçait tout, alors que sur
      // un téléphone Annuler veut dire « ne fais rien ». On libère simplement ;
      // effacer est devenu une demande explicite, et impossible après un départ.
      const choice = confirm(
        'Libérer cette préparation ?\n\n'
        + (totalColis > 0 ? '📦 ' + totalColis + ' colis\n' : '')
        + (photos.length > 0 ? '📸 ' + photos.length + ' photo(s)\n' : '')
        + '\nLe travail déjà fait est conservé : un autre pourra reprendre.\n\n'
        + 'OK pour libérer · Annuler pour revenir en arrière'
      );
      if (!choice) return;

      // Effacement : jamais par défaut, jamais après un départ
      const veutEffacer = estPartie(session)
        ? false
        : confirm('Veux-tu AUSSI effacer les colis et les photos ?\n\n'
            + 'OK = tout effacer (irréversible)\nAnnuler = garder le travail (recommandé)');

      if (veutEffacer) {
        if (!confirm('⚠️ Dernier avertissement : les photos seront supprimées définitivement. Confirmer ?')) return;
        await journaliser(ACTIONS.EFFACEMENT, {
          session, user,
          detail: { colis: totalColis, photos: photos.length, caisses: caisses.map(c => c.code) },
        });
        // Récupérer caisses scannées pour nettoyer stock/mouvements
        const { data: oldCaisses } = await supabase.from('elixir_prep_caisses').select('code').eq('session_id', session.id);
        const oldCodes = (oldCaisses || []).map(c => c.code).filter(Boolean);
        // Supprimer les FICHIERS photos du bucket (sinon orphelins en storage)
        const { data: photoRows } = await supabase.from('elixir_prep_photos').select('storage_path, url').eq('session_id', session.id);
        const photoPaths = (photoRows || []).map(p => p.storage_path || extractPathFromUrl(p.url)).filter(Boolean);
        if (photoPaths.length > 0) {
          await supabase.storage.from('elixir-prep-photos').remove(photoPaths).catch(e => console.warn('Storage cleanup:', e));
        }
        await Promise.all([
          supabase.from('elixir_prep_photos').delete().eq('session_id', session.id),
          supabase.from('elixir_prep_caisses').delete().eq('session_id', session.id),
          supabase.from('elixir_prep_colis').delete().eq('session_id', session.id),
          supabase.from('elixir_prep_anomalies').delete().eq('session_id', session.id),
          supabase.from('elixir_prep_remarks').delete().eq('session_id', session.id),
        ]);
        if (oldCodes.length > 0) {
          await Promise.all([
            supabase.from('elixir_prep_caisse_stock').update({
              status: 'disponible', current_partner_id: null, current_partner_name: null, date_sortie: null,
            }).in('code', oldCodes),
            supabase.from('elixir_prep_caisse_mouvements').update({
              date_retour: new Date().toISOString(),
            }).in('caisse_code', oldCodes).is('date_retour', null),
          ]);
        }
        const reset = {
          prepared_by: null,
          nb_amb: 0, nb_froid: 0, nb_cartons: 0, nb_cartons_vrac: 0,
          nb_amb_manuel: 0, nb_froid_manuel: 0,
          updated_at: new Date().toISOString(),
        };
        await supabase.from('elixir_prep_sessions').update(reset).eq('id', session.id);
        if (onSessionUpdate) onSessionUpdate({ ...session, ...reset });
        onBack();
        return;
      }
    } else {
      if (!confirm('Libérer cette préparation ? Elle redeviendra disponible pour tout le monde.')) return;
    }
    await journaliser(ACTIONS.LIBERATION, { session, user, detail: { colis: totalColis, photos: photos.length } });

    // OK = garder les données
    const { error } = await supabase.from('elixir_prep_sessions').update({
      prepared_by: null, updated_at: new Date().toISOString(),
    }).eq('id', session.id);
    if (error) console.error('releasePrep error:', error);
    if (onSessionUpdate) onSessionUpdate({ ...session, prepared_by: null });
    onBack();
  }

  async function loadAll() {
    setLoading(true);
    await Promise.all([loadProducts(), loadCaisses(), loadOtherCaisses(), loadAnomalies(), loadRemarks(), loadPhotos()]);
    setLoading(false);
  }

  async function loadProducts() {
    try {
      const { data, error } = await supabase.from('elixir_prep_order_lines').select('*').eq('session_id', session.id).order('product_name');
      if (error) throw error;
      const map = {}, list = [];
      (data || []).forEach(line => {
        const prodId = line.product_id || line.id;
        const isRel = (line.order_ref || '').startsWith('[R');
        if (!map[prodId]) { map[prodId] = { id: prodId, name: line.product_name, total_qty: 0, orders: [], is_reliquat: false, price_unit: 0 }; list.push(map[prodId]); }
        if (Number(line.price_unit || 0) > Number(map[prodId].price_unit || 0)) map[prodId].price_unit = Number(line.price_unit);
        map[prodId].total_qty += Number(line.qty) || 0;
        if (line.order_ref && !map[prodId].orders.includes(line.order_ref)) map[prodId].orders.push(line.order_ref);
        if (isRel) map[prodId].is_reliquat = true;
      });
      list.sort((a, b) => {
        if (a.is_reliquat && !b.is_reliquat) return -1;
        if (!a.is_reliquat && b.is_reliquat) return 1;
        return a.name.localeCompare(b.name);
      });
      // FIABILITÉ : Odoo met la quantité à 0 quand le produit n'est pas
      // servi (rupture, non trouvé). Ces lignes ne doivent PAS apparaître
      // dans la liste de travail du préparateur : seuls les produits avec
      // une quantité > 0 sont à préparer. Les autres restent consultables
      // dans une section repliée (traçabilité).
      const visibles = list.filter(p => (Number(p.total_qty) || 0) > 0);
      setProducts(visibles);
      // Drapeaux réglementaires (stupéfiant / cytotoxique) — non bloquant
      getProductFlags(visibles.map(p => p.product_id || p.id)).then(setRegFlags).catch(() => {});
    } catch (e) { console.error('Erreur produits:', e); setError(e.message); }
  }

  async function loadCaisses() {
    try {
      const { data, error } = await supabase.from('elixir_prep_caisses').select('*, scanned_by_user:elixir_prep_users(name)').eq('session_id', session.id).order('scanned_at', { ascending: false });
      if (error) throw error;
      setCaisses(data || []);
    } catch (e) { console.error('Erreur caisses:', e); }
  }

  // Caisses deja ouvertes pour cette pharmacie dans une AUTRE préparation
  // EN COURS : même pharmacie, même jour, pas encore partie. Le cas réel est
  // la pharmacie qui a deux commandes le même jour — on ne veut pas ouvrir
  // une deuxième caisse.
  //
  // Le filtre portait avant sur « statut différent de clôturée » : les
  // préparations facturées des mois précédents restaient dedans, et l'écran
  // affichait des dizaines de caisses parties depuis longtemps.
  async function loadOtherCaisses() {
    if (String(session.id).startsWith('demo-')) return;
    if (!session.partner_id) return;
    try {
      const jour = session.date || new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('elixir_prep_caisses')
        .select('code, type, session_id, elixir_prep_sessions!inner(partner_id, partner_name, status, date)')
        .eq('elixir_prep_sessions.partner_id', session.partner_id)
        .eq('elixir_prep_sessions.date', jour)
        .in('elixir_prep_sessions.status', ['en_cours', 'prete'])
        .neq('session_id', session.id);
      if (error) throw error;
      // Dedup par code
      const seen = new Set();
      const list = [];
      (data || []).forEach(c => {
        if (seen.has(c.code)) return;
        seen.add(c.code);
        list.push({ code: c.code, type: c.type, session_status: c.elixir_prep_sessions?.status });
      });
      setOtherCaisses(list);
    } catch (e) { console.warn('loadOtherCaisses:', e?.message); }
  }

  // Les caisses à récupérer chez la pharmacie ne concernent pas le
  // préparateur mais le chauffeur : elles figurent uniquement sur le bon de
  // transport (colonne « Caisses à récupérer »), remplies au départ.

  async function loadAnomalies() {
    try {
      const { data, error } = await supabase.from('elixir_prep_anomalies').select('*, declared_by_user:elixir_prep_users!declared_by(name)').eq('session_id', session.id).order('declared_at', { ascending: false });
      if (error) throw error;
      setAnomalies(data || []);
    } catch (e) { console.error('Erreur anomalies:', e); }
  }

  async function loadRemarks() {
    try {
      const { data, error } = await supabase.from('elixir_prep_remarks').select('*, declared_by_user:elixir_prep_users!declared_by(name)').eq('session_id', session.id).order('declared_at', { ascending: false });
      if (error) throw error;
      setRemarks(data || []);
    } catch (e) { console.error('Erreur remarques:', e); }
  }

  async function loadPhotos() {
    try {
      const { data, error } = await supabase.from('elixir_prep_photos').select('*, uploaded_by_user:elixir_prep_users!uploaded_by(name)').eq('session_id', session.id).order('uploaded_at', { ascending: false });
      if (error) throw error;
      // URLs signées (12h) via lib/photos — auto-réparation des anciennes
      // URLs publiques http (cause des photos "Load failed")
      const withUrls = await Promise.all((data || []).map(async (p) => {
        let path = p.storage_path || (p.url && !p.url.startsWith('http') ? p.url : extractPathFromUrl(p.url));
        if (!path) return p;
        // Persister le storage_path récupéré depuis l'ancienne URL (one-shot)
        if (!p.storage_path) {
          supabase.from('elixir_prep_photos').update({ storage_path: path }).eq('id', p.id).then(() => {}, () => {});
        }
        const signed = await getSignedUrl(path, 1200);
        return { ...p, storage_path: path, url: signed || p.url };
      }));
      setPhotos(withUrls);
    } catch (e) { console.error('Erreur photos:', e); }
  }

  async function fetchFreshPhotoRefs() {
    const { data, error } = await supabase
      .from('elixir_prep_photos')
      .select('url, storage_path')
      .eq('session_id', session.id)
      .order('uploaded_at', { ascending: false });
    if (error) throw error;
    return (data || [])
      .map(p => ({ storage_path: p.storage_path || (p.url && !p.url.startsWith('http') ? p.url : null), url: p.url }))
      .filter(p => p.storage_path || p.url);
  }

  // ═══════════════════════════════════════
  // SHARED HELPERS
  // ═══════════════════════════════════════

  function showToast(msg, type) {
    setToast({ msg, type });
    try { navigator.vibrate?.(type === 'success' ? [100] : [100, 50, 100]); } catch {}
    playBeep(type);
    setTimeout(() => setToast(null), 2000);
  }

  // Marqueur d'activité : photos/anomalies/remarques ne touchaient pas
  // updated_at → l'auto-release 2h du worker libérait des prépas actives.
  function touchSession() {
    if (String(session.id).startsWith('demo-')) return;
    supabase.from('elixir_prep_sessions')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', session.id)
      .then(() => {}, () => {});
  }

  async function markPreparer() {
    if (session.prepared_by) return;
    if (String(session.id).startsWith('demo-')) {
      if (onSessionUpdate) onSessionUpdate({ ...session, prepared_by: user.id, prepared_by_name: user.name });
      return;
    }
    try {
      await supabase.from('elixir_prep_sessions').update({
        prepared_by: user.id, prepared_by_name: user.name,
      }).eq('id', session.id);
      if (onSessionUpdate) onSessionUpdate({ ...session, prepared_by: user.id, prepared_by_name: user.name });
    } catch (e) { /* silently fail */ }
  }

  // ═══════════════════════════════════════
  // PHOTO ACTIONS
  // ═══════════════════════════════════════

  async function uploadPhoto(file, controlProductId = null) {
    if (!file) return;
    // ═══ DEMO MODE ═══
    if (String(session.id).startsWith('demo-')) {
      const url = URL.createObjectURL(file);
      setPhotos(prev => [...prev, { id: 'demo-' + Date.now(), url, uploaded_at: new Date().toISOString() }]);
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const fileName = session.id + '/' + Date.now() + '_' + Math.random().toString(36).slice(2, 6) + '.' + ext;
      const toSend = await compressImage(file);
      const { error: uploadErr } = await supabase.storage.from('elixir-prep-photos').upload(fileName, toSend, { contentType: toSend.type || file.type });
      if (uploadErr) throw uploadErr;
      // Stocker storage_path comme référence durable (plus d'URL publique)
      const { error: dbErr } = await supabase.from('elixir_prep_photos').insert({ session_id: session.id, url: fileName, storage_path: fileName, uploaded_by: user.id, control_product_id: controlProductId });
      if (dbErr) throw dbErr;
      touchSession();
      await loadPhotos();
      if (session.asana_task_gid) {
        uploadAsanaAttachment(session.asana_task_gid, fileName, file.name || 'photo.jpg')
          .catch(e => console.warn('Asana photo upload error:', e));
      }
    } catch (e) { console.error('Upload photo error:', e); showToast('Erreur upload : ' + e.message, 'error'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  }

  async function removePhoto(photo) {
    if (!confirm('Supprimer cette photo ?')) return;
    if (String(photo.id).startsWith('demo-')) {
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      showToast('Photo supprimée', 'success');
      return;
    }
    try {
      await supabase.storage.from('elixir-prep-photos').remove([photo.storage_path]);
      await supabase.from('elixir_prep_photos').delete().eq('id', photo.id);
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      showToast('Photo supprimée', 'success');
    } catch (e) { showToast('Erreur : ' + e.message, 'error'); }
  }

  // ═══════════════════════════════════════
  // ANOMALY + REMARK ACTIONS
  // ═══════════════════════════════════════

  async function submitAnomaly(product, type, productIncluded, extra = null) {
    if (session.status === 'depart_valide') { showToast('⛔ Commande déjà partie !', 'error'); return; }
    const TYPE_LABELS = { autre_emplacement: 'Autre emplacement', changement_lot: 'Changement de lot', manquant: 'Manquant', produit_abime: 'Abîmé', ecart_plus: 'Écart +', ecart_moins: 'Écart −', peremption: 'Péremption', mauvais_lot: 'Mauvais lot', erreur_ref: 'Erreur réf.', autre: 'Autre' };
    if (String(session.id).startsWith('demo-')) {
      setAnomalies(prev => [...prev, { id: 'demo-a-' + Date.now(), product_name: product.name, anomaly_type: type, product_included: productIncluded, declared_at: new Date().toISOString(), declared_by_user: { name: user.name } }]);
      showToast(product.name.split(' ').slice(0, 3).join(' ') + ' — ' + TYPE_LABELS[type], 'success');
      setSelectedProduct(null); setPendingAnomaly(null); setAnomFilter('');
      return;
    }
    try {
      const ENUM_MAP = { manquant: '1211623105481520', produit_abime: '1211623105481523', ecart_plus: '1211623105481522', ecart_moins: '1211671090017718', peremption: '1211623105481524', mauvais_lot: '1211671481247222', erreur_ref: '1211623105481525', autre: '1211671090017719' };
      // "Pris à un autre emplacement" : Prep corrige la ligne du bon de livraison dans Odoo
      let detail = null, statut = 'ouverte';
      if (type === 'autre_emplacement' && extra?.location) {
        const orderRef = product.order_ref || (String(session.order_refs || '').split(/[,\s]+/).filter(Boolean)[0]) || null;
        let res = null;
        try {
          res = await callAction('moveLineChangeLocation', { orderRef, productId: product.product_id || product.id, newLocationId: extra.location.location_id, newLotId: extra.lot?.lot_id || null });
        } catch (e) { res = { ok: false, reason: 'odoo_error', message: e?.message || String(e) }; }
        detail = { to_location: extra.location.location_name, to_location_id: extra.location.location_id, lot: extra.lot?.lot_name || null,
                   from_location: res?.from || null, from_lot: res?.from_lot || null, picking: res?.picking || null, order_ref: orderRef,
                   odoo_ok: !!res?.ok, odoo_reason: res?.ok ? null : (res?.reason || 'inconnu'), odoo_message: res?.message || null, hors_stock: !!extra.location.horsStock };
        if (!res?.ok || extra.location.horsStock) statut = 'odoo_a_corriger';
      }
      await safeInsert('elixir_prep_anomalies', { session_id: session.id, product_name: product.name, product_id: product.id, anomaly_type: type, product_included: productIncluded, declared_by: user.id, status: statut, asana_enum_gid: ENUM_MAP[type] || null, detail });
      if (type === 'autre_emplacement') {
        if (detail?.odoo_ok && !detail.hors_stock) showToast('✅ Odoo corrigé : ' + (detail.from_location ? detail.from_location + ' → ' : '') + detail.to_location + (detail.lot ? ' · lot ' + detail.lot : ''), 'success');
        else if (detail?.hors_stock) showToast('Enregistré. Odoo n\u2019a pas ce produit à cet emplacement : un responsable vérifiera le stock.', 'warning');
        else showToast('Enregistré, mais Odoo n\u2019a pas pu être corrigé (' + ({ picking_done: 'bon de livraison déjà validé', order_not_found: 'commande introuvable', line_not_found: 'ligne introuvable', no_picking: 'pas de bon de livraison' }[detail?.odoo_reason] || detail?.odoo_message || detail?.odoo_reason) + ') : un responsable corrigera.', 'warning');
      } else {
        showToast(product.name.split(' ').slice(0, 3).join(' ') + ' — ' + TYPE_LABELS[type] + (productIncluded ? ' (mis)' : ' (NON mis)'), 'success');
      }
      touchSession();
      // Anomalie déclarée APRÈS "Prête" : la tâche Asana a déjà été créée au
      // markPrete → pousser l'info en commentaire, sinon elle n'y figure jamais
      if (session.status === 'prete' && session.asana_task_gid) {
        addAsanaComment(session.asana_task_gid,
          '⚠️ Anomalie ajoutée après "Prête" : ' + product.name + ' — ' + TYPE_LABELS[type]
          + (productIncluded ? ' (produit mis)' : ' (produit NON mis)') + ' · par ' + user.name
        ).catch(e => console.warn('Asana comment:', e));
      }
      markPreparer(); setSelectedProduct(null); setPendingAnomaly(null); setAnomFilter('');
      loadAnomalies();
      setTimeout(() => anomScanRef.current?.focus(), 100);
    } catch (e) { showToast('Erreur : ' + e.message, 'error'); }
  }

  // « Pas trouvé à l'emplacement prévu » : mis de côté, rien n'est changé dans
  // Odoo tant que le TSL n'est pas validé (voir PrepAReprendre).
  // Anomalie de stock sur l'emplacement où le produit n'était pas (rien / pas
  // assez → écart −, pas le bon lot → mauvais lot), avec le stock Odoo ailleurs.
  async function declarerStock(product, type, detail) {
    try {
      const ENUM = { ecart_moins: '1211671090017718', mauvais_lot: '1211671481247222' };
      // Une seule anomalie par produit pas trouvé : on garde son id pour la
      // compléter ensuite (changement dans Odoo, ou manquant).
      const { data, error } = await supabase.from('elixir_prep_anomalies').insert({ session_id: session.id, product_name: product.name, product_id: product.id, anomaly_type: type, product_included: true, declared_by: user.id, status: 'ouverte', asana_enum_gid: ENUM[type] || null, detail }).select('id').single();
      if (error) throw error;
      journaliser('a déclaré ne pas trouver un produit en ' + (detail?.emplacement || '?'), { session, user, detail: { produit: product.name, ...detail } });
      markPreparer(); loadAnomalies();
      return data?.id || true;
    } catch (e) { showToast('Erreur : ' + (e?.message || e), 'error'); return false; }
  }

  // « Je ne trouve pas » : assistant plein écran (voir PrepAReprendre).
  function ouvrirPasTrouve(p) {
    setSelectedProduct(null); setPendingAnomaly(null); setAnomFilter('');
    setAssistant({ product: p });
  }

  async function removeAnomaly(id) {
    if (!confirm('Supprimer cette anomalie ?')) return;
    if (String(id).startsWith('demo-')) { setAnomalies(prev => prev.filter(a => a.id !== id)); showToast('Anomalie supprimée', 'success'); return; }
    try {
      const removed = anomalies.find(a => a.id === id);
      await safeDelete('elixir_prep_anomalies', { id });
      setAnomalies(prev => prev.filter(a => a.id !== id));
      showToast('Anomalie supprimée', 'success');
      touchSession();
      if (session.status === 'prete' && session.asana_task_gid && removed) {
        addAsanaComment(session.asana_task_gid,
          '🗑️ Anomalie retirée après "Prête" : ' + (removed.product_name || '?') + ' · par ' + user.name
        ).catch(() => {});
      }
    }
    catch (e) { showToast('Erreur : ' + e.message, 'error'); }
  }

  async function submitRemark() {
    if (session.status === 'depart_valide') { showToast('⛔ Commande déjà partie !', 'error'); return; }
    const text = freeRemark.trim();
    if (!text) return;
    if (String(session.id).startsWith('demo-')) {
      setRemarks(prev => [...prev, { id: 'demo-r-' + Date.now(), text, declared_at: new Date().toISOString(), declared_by_user: { name: user.name } }]);
      showToast('Remarque enregistrée', 'success'); setFreeRemark('');
      return;
    }
    try {
      await safeInsert('elixir_prep_remarks', { session_id: session.id, text, declared_by: user.id });
      showToast('Remarque enregistrée', 'success');
      touchSession();
      if (session.status === 'prete' && session.asana_task_gid) {
        addAsanaComment(session.asana_task_gid,
          '💬 Remarque ajoutée après "Prête" : ' + text + ' · par ' + user.name
        ).catch(() => {});
      }
      markPreparer(); setFreeRemark(''); loadRemarks();
    }
    catch (e) { showToast('Erreur : ' + e.message, 'error'); }
  }

  async function removeRemark(id) {
    if (!confirm('Supprimer cette remarque ?')) return;
    if (String(id).startsWith('demo-')) { setRemarks(prev => prev.filter(r => r.id !== id)); showToast('Remarque supprimée', 'success'); return; }
    try { await safeDelete('elixir_prep_remarks', { id }); setRemarks(prev => prev.filter(r => r.id !== id)); showToast('Remarque supprimée', 'success'); }
    catch (e) { showToast('Erreur : ' + e.message, 'error'); }
  }

  // ═══════════════════════════════════════
  // CAISSE ACTIONS
  // ═══════════════════════════════════════

  async function addCaisse(type) {
    if (session.status === 'depart_valide') { showToast('⛔ Commande déjà partie en livraison !', 'error'); return; }
    let code = newCaisse.trim().toUpperCase();
    if (!code) return;
    if (!type) { showToast('Choisis Ambiant ou Froid', 'error'); return; }
    code = code.replace(/[\s_.]/g, '');
    // C42, 42, et la forme « deuxième caisse avec la même étiquette » C42-2
    const match = code.match(/^C?0*(\d+)(?:-(\d))?$/);
    if (!match) { showToast('Code invalide ! Format attendu : C1, C42, C5468…', 'error'); setNewCaisse(''); return; }
    code = 'C' + match[1] + (match[2] ? '-' + match[2] : '');
    // Caisse deja dans CETTE session -> confirmee, rien ne s ajoute
    if (caisses.find(c => c.code === code)) {
      showToast('✅ ' + code + ' confirmée — tu continues dedans', 'success');
      setNewCaisse(''); setNewCaisseType(null);
      setTimeout(() => scanRef.current?.focus(), 100);
      return;
    }
    // Caisse deja ouverte pour cette pharmacie dans une AUTRE session non cloturee
    // -> on confirme (le magasinier continue dedans), rien ne s ajoute a SA session
    const existingOther = otherCaisses.find(c => c.code === code);
    if (existingOther) {
      showToast('✅ ' + code + ' déjà ouverte pour cette pharmacie — tu continues dedans', 'success');
      setNewCaisse(''); setNewCaisseType(null);
      setTimeout(() => scanRef.current?.focus(), 100);
      return;
    }
    // ═══ DEMO MODE ═══
    if (String(session.id).startsWith('demo-')) {
      const newCaisses = [{ code, type, scanned_at: new Date().toISOString(), scanned_by_user: { name: user.name } }, ...caisses];
      setCaisses(newCaisses);
      showToast(code + ' (' + (type === 'ambiant' ? 'amb' : 'froid') + ') ajoutée !', 'success');
      setNewCaisse(''); setNewCaisseType(null);
      setTimeout(() => scanRef.current?.focus(), 100);
      return;
    }
    // Vérifier doublon dans une autre pharmacie
    try {
      const sessionDate = session.date || new Date().toISOString().split('T')[0];
      const { data: otherUse } = await supabase.from('elixir_prep_caisses')
        .select('id, session_id, elixir_prep_sessions!inner(partner_name, date)')
        .eq('code', code)
        .eq('elixir_prep_sessions.date', sessionDate)
        .neq('session_id', session.id)
        .limit(1);
      if (otherUse && otherUse.length > 0) {
        const otherPharma = otherUse[0].elixir_prep_sessions?.partner_name || 'une autre pharmacie';
        // Avant : showToast bloquant. Maintenant : confirm pour forcer la recuperation
        // (cas : on n a pas scanne le retour mais la caisse est physiquement revenue)
        if (!confirm('⚠️ ' + code + ' est encore enregistree chez ' + otherPharma + ' aujourd hui.\n\nElle est revenue et tu veux la reutiliser pour ' + session.partner_name + ' ?')) {
          setNewCaisse(''); return;
        }
        // Detacher de l autre session : supprimer le lien dans elixir_prep_caisses
        await supabase.from('elixir_prep_caisses').delete().eq('id', otherUse[0].id);
        // Cloturer le mouvement ouvert (date_retour = maintenant)
        await supabase.from('elixir_prep_caisse_mouvements')
          .update({ date_retour: new Date().toISOString(), scanned_by_retour: user.id })
          .eq('caisse_code', code).is('date_retour', null);
        showToast('Caisse recuperee de ' + otherPharma, 'info');
      }
    } catch (e) { /* offline */ }
    // Vérifier si en livraison ou chez pharmacie
    try {
      const { data: stock } = await supabase.from('elixir_prep_caisse_stock').select('status, current_partner_name, date_sortie').eq('code', code).maybeSingle();
      if (stock && stock.status !== 'disponible') {
        const chez = stock.current_partner_name || 'une pharmacie';
        const jours = stock.date_sortie ? Math.floor((new Date() - new Date(stock.date_sortie)) / 86400000) : '?';
        const statusLabel = stock.status === 'en_livraison' ? 'en livraison' : stock.status;
        if (!confirm('⚠️ ' + code + ' est marquée "' + statusLabel + '" chez ' + chez + ' (depuis ' + jours + 'j).\n\nL\'utiliser quand même ?')) { setNewCaisse(''); return; }
        await supabase.from('elixir_prep_caisse_stock').update({ status: 'disponible', current_partner_id: null, current_partner_name: null, date_sortie: null }).eq('code', code);
        await supabase.from('elixir_prep_caisse_mouvements').update({ date_retour: new Date().toISOString(), scanned_by_retour: user.id }).eq('caisse_code', code).is('date_retour', null);
      }
    } catch (e) { /* offline */ }
    // Insérer
    try {
      // Insert direct (pas safeInsert qui avale les erreurs)
      const { error: insertErr } = await supabase.from('elixir_prep_caisses').insert({ session_id: session.id, code, type, scanned_by: user.id });
      if (insertErr) { showToast('❌ Erreur caisse : ' + insertErr.message, 'error'); setNewCaisse(''); return; }

      const newCaisses = [{ code, type, scanned_by: user.id, scanned_at: new Date().toISOString(), scanned_by_user: { name: user.name } }, ...caisses];
      setCaisses(newCaisses);
      showToast(code + ' (' + (type === 'ambiant' ? 'amb' : 'froid') + ') ajoutée !', 'success');
      markPreparer(); setNewCaisse(''); setNewCaisseType(null);
      setTimeout(() => scanRef.current?.focus(), 100);
      // Affichage optimiste — les compteurs RÉELS sont recalculés en base
      // par le trigger prep_caisses_recount (sql/007), plus d'écriture
      // client (fini le last-write-wins entre magasiniers)
      const nbAmbLocal = newCaisses.filter(c => c.type === 'ambiant').length;
      const nbFroidLocal = newCaisses.filter(c => c.type === 'froid').length;
      if (onSessionUpdate) onSessionUpdate({ ...session, nb_amb: nbAmbLocal, nb_froid: nbFroidLocal });

      // Écritures tracking caisse — les échecs sont VISIBLES (plus avalés)
      const trackingResults = await Promise.allSettled([
        supabase.from('elixir_prep_caisse_stock').upsert({ code, status: 'en_livraison', current_partner_id: session.partner_id, current_partner_name: session.partner_name, date_sortie: new Date().toISOString() }, { onConflict: 'code' }),
        supabase.from('elixir_prep_caisse_mouvements').insert({ caisse_code: code, partner_id: session.partner_id, partner_name: session.partner_name, tournee: session.tournee || '', type, date_depart: new Date().toISOString(), scanned_by_depart: user.id, session_id: session.id }),
      ]);
      const trackingFailed = trackingResults.some(r => r.status === 'rejected' || r.value?.error);
      if (trackingFailed) {
        console.warn('Tracking caisse:', trackingResults);
        showToast('⚠️ ' + code + ' ajoutée mais tracking caisse non enregistré — rescanne ou préviens un responsable', 'error');
      }
    } catch (e) { showToast('❌ Erreur : ' + e.message, 'error'); }
  }

  // Verifie si un code correspond a une caisse deja existante (cette session ou
  // une autre session non cloturee de la pharmacie). Si oui : confirme, rien ne s ajoute.
  function tryConfirmExisting(rawCode) {
    let code = (rawCode || '').trim().toUpperCase().replace(/[\s_.]/g, '');
    const match = code.match(/^C?0*(\d+)(?:-(\d))?$/);
    if (!match) return false;
    code = 'C' + match[1] + (match[2] ? '-' + match[2] : '');
    if (caisses.find(c => c.code === code)) {
      // Deux caisses peuvent porter physiquement la même étiquette : on demande,
      // au lieu de supposer que c'est la même et de n'enregistrer qu'une seule.
      let libre = null;
      for (let i = 2; i <= 9 && !libre; i++) if (!caisses.find(c => c.code === code + '-' + i)) libre = code + '-' + i;
      const deuxieme = libre && confirm(code + ' est déjà sur cette préparation.\n\n'
        + 'C\'est une DEUXIÈME caisse avec la même étiquette ?\n\n'
        + 'OK = l\'enregistrer sous ' + libre + ' (étiquette à refaire)\n'
        + 'Annuler = c\'est bien la même, je continue dedans');
      if (deuxieme) { setNewCaisse(libre); return false; }   // repasse par l'ajout normal
      showToast('✅ ' + code + ' confirmée — tu continues dedans', 'success');
      setNewCaisse(''); setNewCaisseType(null);
      setTimeout(() => scanRef.current?.focus(), 100);
      return true;
    }
    if (otherCaisses.find(c => c.code === code)) {
      showToast('✅ ' + code + ' déjà ouverte pour cette pharmacie — tu continues dedans', 'success');
      setNewCaisse(''); setNewCaisseType(null);
      setTimeout(() => scanRef.current?.focus(), 100);
      return true;
    }
    return false;
  }

  function handleScanKey(e) {
    if (e.key === 'Enter' && newCaisse.trim()) {
      // Si la caisse existe deja -> confirmation directe, pas besoin de type
      if (tryConfirmExisting(newCaisse)) return;
      if (!newCaisseType) { showToast('⚠️ Choisis d\'abord AMBIANT ou FROID !', 'error'); return; }
      addCaisse(newCaisseType);
    }
  }

  async function removeCaisse(caisse) {
    if (!confirm('Retirer ' + caisse.code + ' ?')) return;
    try {
      await supabase.from('elixir_prep_caisses').delete().eq('session_id', session.id).eq('code', caisse.code);
      // Remettre la caisse en disponible et fermer le mouvement ouvert
      await Promise.all([
        supabase.from('elixir_prep_caisse_stock').update({
          status: 'disponible', current_partner_id: null, current_partner_name: null, date_sortie: null,
        }).eq('code', caisse.code),
        supabase.from('elixir_prep_caisse_mouvements').update({
          date_retour: new Date().toISOString(), scanned_by_retour: user.id,
        }).eq('caisse_code', caisse.code).eq('session_id', session.id).is('date_retour', null),
      ]);
      const newCaisses = caisses.filter(c => c.code !== caisse.code);
      setCaisses(newCaisses);
      // Affichage optimiste — compteurs réels recalculés par le trigger (sql/007)
      const nbAmbNew = newCaisses.filter(c => c.type === 'ambiant').length;
      const nbFroidNew = newCaisses.filter(c => c.type === 'froid').length;
      if (onSessionUpdate) onSessionUpdate({ ...session, nb_amb: nbAmbNew, nb_froid: nbFroidNew });
    } catch (e) { showToast('Erreur : ' + e.message, 'error'); }
  }

  async function updateCartonsStandard(delta) {
    if (session.status === 'depart_valide') { showToast('⛔ Commande déjà partie !', 'error'); return; }
    const val = Math.max(0, nbCartons + delta);
    setNbCartons(val);
    if (!String(session.id).startsWith('demo-')) {
      await safeUpdate('elixir_prep_sessions', { nb_cartons: val, nb_cartons_vrac: 0, updated_at: new Date().toISOString() }, { id: session.id });
    }
    if (onSessionUpdate) onSessionUpdate({ ...session, nb_cartons: val, nb_cartons_vrac: 0 });
    markPreparer();
  }

  // ═══ DIRECT CAISSE COUNT (mode sans étiquettes) ═══
  // Ajustement manuel : le magasinier saisit le TOTAL physique (caisses
  // scannées + caisses sans étiquette). La part manuelle est déduite en
  // base par set_prep_caisse_total (sql/007), atomiquement, à partir du
  // comptage réel des scannées. Plancher = nombre de scannées (pour en
  // Le total de caisses n'est plus saisissable à la main : une caisse existe
  // par son numéro (scanné ou tapé), jamais par un compteur. Seuls les cartons
  // se comptent sans identifiant.

  // ═══════════════════════════════════════
  // ASANA + VALIDATION
  // ═══════════════════════════════════════

  async function handleSyncAsana() {
    try {
      const freshPhotos = await fetchFreshPhotoRefs().catch(() => photos);
      const taskGid = await syncSessionToAsana(session, user, caisses, anomalies, remarks, freshPhotos, nbCartons, 0);
      await supabase.from('elixir_prep_sessions').update({ asana_task_gid: taskGid, updated_at: new Date().toISOString() }).eq('id', session.id);
      // NE PAS appeler onSessionUpdate ici — on a déjà quitté la page
    } catch (e) { console.warn('Asana sync error:', e); }
  }

  async function handleMarkPrete() {
    if (String(session.id).startsWith('demo-')) {
      if (onSessionUpdate) onSessionUpdate({ ...session, status: 'prete' });
      showToast('✅ Pharmacie prête à partir !', 'success');
      setTimeout(() => onBack(), 600);
      return;
    }
    if (controleAvantPrete) {
      const r = await controleAvantPrete();
      if (!r?.ok) { showToast(r?.message || 'Préparation non terminée', 'error'); return; }
    }
    // Produits pas trouvés encore « à reprendre » : la préparation n'est pas finie.
    try {
      const { data: ar, error: arErr } = await supabase.from('elixir_prep_a_reprendre').select('product_name')
        .eq('session_id', session.id).in('statut', A_REPRENDRE_EN_COURS);
      if (!arErr && ar?.length) {
        showToast('⛔ ' + ar.length + ' produit(s) à reprendre : ' + ar.map(a => (a.product_name || '').split(' ').slice(0, 3).join(' ')).join(', ') + ' — reprends-les ou déclare-les manquants', 'error');
        return;
      }
    } catch { /* table absente : on ne bloque pas */ }
    setSyncing(true);
    try {
      // Synchroniser la file offline AVANT de valider (ex. nb_cartons saisi
      // hors ligne). On ne purge plus : purger = perdre les saisies.
      await flushQueue();
      const stillPending = getSessionPendingCount(session.id);
      if (stillPending > 0) {
        showToast('⚠️ ' + stillPending + ' saisie(s) hors ligne non synchronisée(s) — réseau requis avant de valider', 'error');
        setSyncing(false);
        return;
      }

      // ═══ CONTRÔLE RENFORCÉ PRODUITS CHERS ═══
      // Photo du produit dans la caisse OBLIGATOIRE pour chaque produit
      // cher/sensible avant de marquer la prépa prête.
      const chersManquants = products.filter(p => isProduitCher(p)
        && !photos.some(ph => String(ph.control_product_id) === String(p.id)));
      if (chersManquants.length > 0) {
        showToast('⚠️ Photo du produit dans la caisse obligatoire : '
          + chersManquants.map(p => (p.name || '').split(' ').slice(0, 2).join(' ')).join(', ')
          + ' — étape 2 Photos, bouton 📷 Photo caisse', 'error');
        setSyncing(false);
        return;
      }

      // Une caisse DOIT porter un numéro — scanné ou tapé. Sans numéro elle
      // sort du dépôt sans exister : impossible de la réclamer ensuite à la
      // pharmacie. Un carton, lui, n'a pas de numéro : il ne bloque rien.
      const identifiees = caisses.length;
      const declarees = (nbAmb || 0) + (nbFroid || 0);
      if (declarees > identifiees) {
        showToast('⛔ ' + (declarees - identifiees) + ' caisse(s) sans numéro — scanne-la ou tape son numéro à la main avant de valider (un carton seul ne bloque pas)', 'error');
        setSyncing(false);
        return;
      }

      const { error, count } = await supabase.from('elixir_prep_sessions').update({
        status: 'prete', updated_at: new Date().toISOString(), prete_at: new Date().toISOString(),
      }).eq('id', session.id).eq('status', 'en_cours').select('id', { count: 'exact', head: true });

      if (error) throw error;
      if (count === 0) {
        showToast('⚠️ Session déjà validée ou modifiée', 'error');
        setSyncing(false);
        return;
      }

      // Succès — mettre à jour l'état local ET revenir à la liste
      if (onSessionUpdate) onSessionUpdate({ ...session, status: 'prete' });
      showToast('✅ Prête à partir !', 'success');
      onBack();
      // Asana en arrière-plan
      handleSyncAsana().catch(() => {});
    } catch (e) {
      console.error('handleMarkPrete error:', e);
      showToast('❌ Erreur : ' + (e.message || 'Échec'), 'error');
    }
    finally { setSyncing(false); }
  }

  async function handleResetEnCours() {
    if (String(session.id).startsWith('demo-')) {
      if (onSessionUpdate) onSessionUpdate({ ...session, status: 'en_cours' });
      showToast('Remise en préparation', 'success');
      return;
    }
    const { error } = await supabase.from('elixir_prep_sessions').update({
      status: 'en_cours', updated_at: new Date().toISOString(),
    }).eq('id', session.id);
    if (error) { showToast('Erreur : ' + error.message, 'error'); return; }
    if (onSessionUpdate) onSessionUpdate({ ...session, status: 'en_cours' });
    showToast('Remise en préparation', 'success');
  }

  async function deleteSession() {
    if (!confirm('🗑️ Supprimer ' + session.partner_name + ' ?\n\nToutes les données seront effacées (photos, caisses, anomalies, remarques).')) return;
    if (!confirm('⚠️ Confirmer la suppression définitive de ' + session.partner_name + ' ?')) return;
    try {
      // Fichiers photos du bucket d'abord (sinon orphelins en storage)
      const { data: photoRows } = await supabase.from('elixir_prep_photos').select('storage_path, url').eq('session_id', session.id);
      const photoPaths = (photoRows || []).map(p => p.storage_path || extractPathFromUrl(p.url)).filter(Boolean);
      if (photoPaths.length > 0) {
        await supabase.storage.from('elixir-prep-photos').remove(photoPaths).catch(e => console.warn('Storage cleanup:', e));
      }
      await Promise.all([
        supabase.from('elixir_prep_photos').delete().eq('session_id', session.id),
        supabase.from('elixir_prep_caisses').delete().eq('session_id', session.id),
        supabase.from('elixir_prep_colis').delete().eq('session_id', session.id),
        supabase.from('elixir_prep_anomalies').delete().eq('session_id', session.id),
        supabase.from('elixir_prep_remarks').delete().eq('session_id', session.id),
        supabase.from('elixir_prep_order_lines').delete().eq('session_id', session.id),
      ]);
      // NOTE: on ne touche PAS caisse_stock ni mouvements
      // Vraie suppression (plus de recyclage en 'facturee', sémantiquement faux).
      // Si la commande est toujours active dans Odoo, le worker recréera une
      // session vierge au prochain sync — comportement miroir assumé.
      await supabase.from('elixir_prep_sessions').delete().eq('id', session.id);
      onBack();
    } catch (e) { showToast('Erreur : ' + e.message, 'error'); }
  }

  // ═══════════════════════════════════════
  // COMPUTED
  // ═══════════════════════════════════════

  const tColor = { '1': '#1A5276', '2': '#1E6B3C', '3': '#7D5A0A', '4': '#5B2C8D', '5': '#922B21', '6': '#117A65', 'IMPERATIFS': '#B03A2E' }[session.tournee] || '#888';
  const totalQty = products.reduce((s, p) => s + p.total_qty, 0);
  // Caisses: prefer session counts (counter mode)
  const nbAmb = session.nb_amb != null ? session.nb_amb : caisses.filter(c => c.type === 'ambiant').length;
  const nbFroid = session.nb_froid != null ? session.nb_froid : caisses.filter(c => c.type === 'froid').length;

  // ═══════════════════════════════════════
  // RENDER — Flow guidé compact (Option A)
  // ═══════════════════════════════════════

  const totalColis = nbAmb + nbFroid + nbCartons;
  const stepCaissesDone = totalColis > 0;
  const stepPhotosDone = photos.length > 0;

  function toggleStep(s) {
    setOpenStep(openStep === s ? null : s);
  }

  return (
    <div>
      {toast && (
        <div style={{
          position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)',
          padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600,
          color: 'white', zIndex: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          background: toast.type === 'success' ? '#1E6B3C' : '#C0392B',
        }}>{toast.msg}</div>
      )}

      <PrepHeader session={session} tColor={tColor} products={products} totalQty={totalQty}
        nbAmb={nbAmb} nbFroid={nbFroid} nbCartons={nbCartons} nbCartonsVrac={0}
        reliquats={reliquats} newCaisse={newCaisse} onBack={onBack}
        onRelease={null} />

      {/* ═══ CLAIM — "Je prends cette prépa" ═══ */}
      {!isClaimed && session.status !== 'prete' && session.status !== 'depart_valide' && (
        <div style={{ padding: '16px 0' }}>
          {/* Show products as preview */}
          {products.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', marginBottom: 4 }}>Produits à préparer ({products.length})</div>
              {products.some(p => hasAnyFlag(regFlags[p.product_id || p.id])) && (() => {
                const n = (k) => products.filter(p => regFlags[p.product_id || p.id]?.[k]).length;
                return (
                  <div style={{ padding: '8px 10px', borderRadius: 8, background: '#FDECEA', border: '1.5px solid #C0392B', marginBottom: 4, fontSize: 11, fontWeight: 800, color: '#C0392B' }}>
                    {n('froid') > 0 && <div style={{ color: '#1A5276' }}>❄️ {n('froid')} produit(s) FROID : caisse isotherme + accumulateurs, ne pas laisser hors du frigo</div>}
                    {n('stup') > 0 && <div>🔒 {n('stup')} stupéfiant(s) : armoire sécurisée, double contrôle, traçabilité du registre</div>}
                    {n('psy') > 0 && <div style={{ color: '#B7791F' }}>⚠️ {n('psy')} psychotrope(s) : stockage sécurisé, vigilance sur les quantités</div>}
                    {n('mds') > 0 && <div style={{ color: '#1A5276' }}>🩸 {n('mds')} MDS (dérivé du sang) : traçabilité lot obligatoire jusqu’au patient</div>}
                    {n('cyto') > 0 && <div style={{ color: '#6B21A8' }}>☣️ {n('cyto')} cytotoxique(s) : manipulation avec précaution, emballage séparé</div>}
                  </div>
                );
              })()}
              {products.some(p => isProduitCher(p)) && (
                <div style={{ padding: '8px 10px', borderRadius: 8, background: '#FDFAF4', border: '1.5px solid #b5904a', marginBottom: 4, fontSize: 11, fontWeight: 800, color: '#8a6a30' }}>
                  ⚠️ {products.filter(p => isProduitCher(p)).length} produit{products.filter(p => isProduitCher(p)).length > 1 ? 's' : ''} sensible{products.filter(p => isProduitCher(p)).length > 1 ? 's' : ''} : photo du produit dans la caisse obligatoire
                </div>
              )}
              {products.map((p, idx) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                  background: isProduitCher(p) ? '#FDFAF4' : p.is_reliquat ? '#FFF5F5' : 'white', border: '0.5px solid ' + (isProduitCher(p) ? '#b5904a' : p.is_reliquat ? '#F5B7B1' : '#E8E8EE'), borderRadius: 8, marginBottom: 2,
                }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF' }}>{idx + 1}</span>
                  {isProduitCher(p) && <span style={{ fontSize: 7, fontWeight: 700, padding: '2px 4px', borderRadius: 3, background: '#FDFAF4', color: '#8a6a30', border: '0.5px solid #b5904a' }}>SENSIBLE</span>}
                  {flagBadges(React, regFlags[p.product_id || p.id], 8)}
                  {p.is_reliquat && <span style={{ fontSize: 7, fontWeight: 700, padding: '2px 4px', borderRadius: 3, background: '#FDEDEC', color: '#C0392B' }}>REL</span>}
                  <span style={{ flex: 1, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || p.product_name}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: p.is_reliquat ? '#C0392B' : '#1A5276' }}>{p.total_qty}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1D23', marginBottom: 4 }}>Tape le bouton pour commencer cette préparation</div>
            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 12 }}>Tout le monde verra que tu es dessus</div>
          </div>
          <button onClick={claimPrep} style={{
            width: '100%', padding: 16, borderRadius: 12, border: 'none',
            background: tColor, color: 'white', fontSize: 16, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Je prends cette prépa</button>
        </div>
      )}

      {/* ═══ CLAIMED BY SOMEONE ELSE ═══ */}
      {claimedByOther && session.status !== 'prete' && session.status !== 'depart_valide' && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 10,
          background: '#FFF8F0', border: '2px solid #F0C36D',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#92710C' }}>
            ⚠️ En cours de préparation par {session.prepared_by_name || '?'}
          </div>
          <div style={{ fontSize: 11, color: '#92710C', marginTop: 2 }}>Tu peux quand même consulter et modifier si besoin.</div>
        </div>
      )}

      {/* ═══ CONTENT — only when claimed or already done ═══ */}
      {(isClaimed || session.status === 'prete' || session.status === 'depart_valide') && <>

      {/* ═══ ALERTE PRODUITS SENSIBLES — visible immédiatement ═══ */}
      {(() => {
        const chers = products.filter(p => isProduitCher(p));
        if (chers.length === 0) return null;
        const faits = chers.filter(p => photos.some(ph => String(ph.control_product_id) === String(p.id)));
        const tousFaits = faits.length === chers.length;
        return (
          <div onClick={() => setOpenStep('photos')} style={{
            marginBottom: 8, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
            background: tousFaits ? '#D5F5E3' : '#FDFAF4',
            border: '2px solid ' + (tousFaits ? '#16A34A' : '#b5904a'),
            boxShadow: tousFaits ? 'none' : '0 2px 10px rgba(181,144,74,0.3)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: tousFaits ? '#166534' : '#8a6a30' }}>
              {tousFaits
                ? '✅ ' + chers.length + ' produit' + (chers.length > 1 ? 's' : '') + ' sensible' + (chers.length > 1 ? 's' : '') + ' : photos dans la caisse prises'
                : '⚠️ ' + chers.length + ' PRODUIT' + (chers.length > 1 ? 'S' : '') + ' SENSIBLE' + (chers.length > 1 ? 'S' : '') + ' DANS CETTE PRÉPA'}
            </div>
            {!tousFaits && (
              <>
                <div style={{ fontSize: 11, color: '#8a6a30', marginTop: 4, fontWeight: 600 }}>
                  Une photo de chaque produit POSÉ DANS LA CAISSE est obligatoire avant de valider ({faits.length}/{chers.length} prise{faits.length > 1 ? 's' : ''})
                </div>
                <div style={{ fontSize: 11, color: '#1a2322', marginTop: 4 }}>
                  {chers.map(p => {
                    const ok = photos.some(ph => String(ph.control_product_id) === String(p.id));
                    return <div key={p.id} style={{ marginTop: 2 }}>{ok ? '✅' : '📷'} {p.name}</div>;
                  })}
                </div>
                <div style={{ fontSize: 10, color: '#B7791F', marginTop: 6, fontStyle: 'italic' }}>Les photos se prennent à l'étape 2 — Photos</div>
              </>
            )}
          </div>
        );
      })()}

      {/* Produits — repliable */}
      <div style={{ marginBottom: 6 }}>
        <div onClick={() => setProductsOpen(!productsOpen)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
          background: 'white', borderRadius: 10, border: '0.5px solid #E8E8EE',
          cursor: 'pointer', userSelect: 'none',
        }}>
          <span style={{ fontSize: 10, color: '#9CA3AF', transform: productsOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▼</span>
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Produits</span>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>{products.length} produit{products.length > 1 ? 's' : ''} · {totalQty} boîte{totalQty > 1 ? 's' : ''}</span>
        </div>
        {productsOpen && (
          <div style={{ padding: '8px 0 0' }}>
            <PrepProduitsTab products={products} totalQty={totalQty} loading={loading} error={error} onRetry={loadAll}
              photos={photos} onControlPhoto={(productId, file) => uploadPhoto(file, productId)}
              onPasTrouve={aReprendreDispo && isMyPrep && !['prete', 'depart_valide'].includes(session.status) ? ouvrirPasTrouve : null}
              idsDeCote={idsDeCote} />
          </div>
        )}
      </div>

      {/* Label section */}
      <div style={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 0 6px 2px' }}>Préparation</div>

      <PrepAReprendre session={session} user={user} products={products} isMyPrep={isMyPrep} showToast={showToast}
        version={versionAReprendre} onItems={(its) => setIdsDeCote(its.map(i => String(i.product_id)))} onAnomalies={loadAnomalies} onManquant={(p) => submitAnomaly(p, 'manquant', false)}
        assistant={assistant} onAssistant={setAssistant} onDeclarerStock={declarerStock} />

      {/* Étape 1 — Caisses */}
      <div style={{ marginBottom: 4 }}>
        <StepRow openStep={openStep} onToggle={toggleStep} id="caisses" num="1" title="Caisses & cartons" subtitle="Nombre amb/froid + cartons" count={totalColis} done={stepCaissesDone} />
        <StepContent openStep={openStep} id="caisses">

          {/* ═══ Caisses deja ouvertes pour cette pharmacie (autres sessions) ═══ */}
          {otherCaisses.length > 0 && (
            <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: '#FFF8E1', border: '1px solid #F0C36D' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92710C', marginBottom: 4 }}>
                📦 Caisse(s) déjà ouverte(s) pour cette pharmacie
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {otherCaisses.map(c => (
                  <span key={c.code} style={{
                    padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                    background: c.type === 'froid' ? '#D6EAF8' : '#FDEBD0',
                    color: c.type === 'froid' ? '#1A5276' : '#D4680A',
                  }}>{c.code} {c.type === 'froid' ? '❄️' : '📦'}</span>
                ))}
              </div>
              <div style={{ fontSize: 10, color: '#92710C', marginTop: 4, fontStyle: 'italic' }}>
                Scanne-la pour confirmer que tu continues dedans (rien ne sera ajouté).
              </div>
            </div>
          )}

          {/* ═══ SCAN CAISSE — en premier, gros ═══ */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1A5276', marginBottom: 6 }}>📷 SCANNER UNE CAISSE</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input
                ref={scanRef}
                type="text"
                inputMode="text"
                value={newCaisse}
                onChange={e => setNewCaisse(e.target.value.toUpperCase())}
                onKeyDown={handleScanKey}
                placeholder="N° caisse (ex: C1, C42…)"
                style={{ flex: 1, padding: '14px 14px', borderRadius: 10, border: '2px solid #1A5276', fontSize: 16, fontWeight: 600, fontFamily: 'inherit' }}
              />
              <button onClick={() => setScannerOpen(true)} style={{
                padding: '0 18px', borderRadius: 10, border: '2px solid #1A5276',
                background: '#1A5276', color: 'white', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 22,
              }}>📷</button>
            </div>

            {/* Étiquette illisible : l'app donne un numéro neuf plutôt que de
                laisser partir une caisse non suivie */}
            {!newCaisse.trim() && (
              <button onClick={async () => {
                try {
                  const code = await attribuerNumero();
                  if (code) { setNewCaisse(code); showToast('Écris ' + code + ' sur la caisse, puis choisis AMB ou FROID', 'info'); }
                } catch (e) { showToast('Erreur : ' + (e?.message || e), 'error'); }
              }} style={{
                width: '100%', padding: '10px 12px', borderRadius: 9, marginBottom: 8, cursor: 'pointer',
                border: '1.5px dashed #D4680A', background: '#FFF8F0', color: '#D4680A',
                fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              }}>🏷️ Étiquette illisible ou arrachée — donner un numéro</button>
            )}

            {/* Boutons AMB / FROID — gros, apparaissent apres saisie d un nouveau numero */}
            {newCaisse.trim() && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <button onClick={() => { setNewCaisseType('ambiant'); addCaisse('ambiant'); }}
                  style={{ flex: 1, padding: '16px', borderRadius: 10, border: '2px solid #1A5276', background: '#EEF6FF', cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: '#1A5276' }}>
                  📦 AMBIANT
                </button>
                <button onClick={() => { setNewCaisseType('froid'); addCaisse('froid'); }}
                  style={{ flex: 1, padding: '16px', borderRadius: 10, border: '2px solid #0E6655', background: '#E8F8F5', cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: '#0E6655' }}>
                  ❄️ FROID
                </button>
              </div>
            )}

            {/* Liste caisses scannées (cette session) */}
            {caisses.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {caisses.map(c => (
                  <div key={c.code || c.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
                    background: c.type === 'froid' ? '#F0F7FF' : '#FFF8F0', borderRadius: 8,
                    border: '0.5px solid ' + (c.type === 'froid' ? '#B3D4F0' : '#F5CBA7'), marginBottom: 3, fontSize: 13,
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: c.type === 'froid' ? '#1A5276' : '#D4680A' }}>{c.code}</span>
                    <span style={{ color: '#888', flex: 1 }}>{c.type === 'froid' ? '❄️ Froid' : '📦 Ambiant'}</span>
                    <span style={{ color: '#AAA', fontSize: 10 }}>{c.scanned_by_user?.name || ''}</span>
                    <button onClick={() => removeCaisse(c)} style={{
                      background: 'none', border: 'none', color: '#C0392B', fontSize: 16, cursor: 'pointer', padding: '2px 6px',
                    }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ═══ Récapitulatif colis + cartons ═══ */}
          <div style={{ borderTop: '1px dashed #E8E8EE', paddingTop: 10 }}>
            <PrepCaissesSection session={session} user={user} caisses={caisses} nbAmb={nbAmb} nbFroid={nbFroid}
              nbCartons={nbCartons}
              onUpdateCartons={updateCartonsStandard} />
          </div>
        </StepContent>
      </div>

      {/* Étape 2 — Photos */}
      <div style={{ marginBottom: 4 }}>
        <StepRow openStep={openStep} onToggle={toggleStep} id="photos" num="2" title="Photos" subtitle="Produits + étiquette" count={photos.length} done={stepPhotosDone} />
        <StepContent openStep={openStep} id="photos">
          <PrepPhotosSection photos={photos} uploading={uploading} fileInputRef={fileInputRef} onUpload={uploadPhoto} onRemove={removePhoto} onRefreshUrls={loadPhotos}
            products={products} onControlPhoto={(productId, file) => uploadPhoto(file, productId)} />
        </StepContent>
      </div>

      {/* Étape 3 — Anomalies */}
      <div id="etape-anomalies" style={{ marginBottom: 4 }} ref={initialStep === 'anomalies' ? (el) => {
        // Une seule fois, à l'ouverture : la fonction ref est rappelée à chaque
        // rendu, et faisait remonter la page en plein choix (constaté le 21/10/2026).
        if (el && !loading && !dejaDefile.current) { dejaDefile.current = true; setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300); }
      } : undefined}>
        <StepRow openStep={openStep} onToggle={toggleStep} id="anomalies" num="3" title="Anomalies & remarques" subtitle="Facultatif" count={anomalies.length > 0 ? anomalies.length : null} done={false} warning={anomalies.length > 0} />
        <StepContent openStep={openStep} id="anomalies">
          <PrepAnomaliesSection products={products} anomalies={anomalies} remarks={remarks}
            selectedProduct={selectedProduct} setSelectedProduct={setSelectedProduct}
            pendingAnomaly={pendingAnomaly} setPendingAnomaly={setPendingAnomaly}
            anomFilter={anomFilter} setAnomFilter={setAnomFilter}
            freeRemark={freeRemark} setFreeRemark={setFreeRemark} anomScanRef={anomScanRef}
            onSubmitAnomaly={submitAnomaly} onRemoveAnomaly={removeAnomaly}
            onSubmitRemark={submitRemark} onRemoveRemark={removeRemark} onPasTrouve={aReprendreDispo && isMyPrep ? ouvrirPasTrouve : null}
            orderRefs={Array.isArray(session.order_refs) ? session.order_refs : String(session.order_refs || '').split(/[,\s]+/).filter(Boolean)} />
        </StepContent>
      </div>

      {/* Validation */}
      <PrepValidation session={session} nbAmb={nbAmb} nbFroid={nbFroid} photos={photos}
        nbCartons={nbCartons} nbCartonsVrac={0} syncing={syncing}
        onMarkPrete={handleMarkPrete} onResetEnCours={handleResetEnCours} showToast={showToast} />

      {/* Libérer — en bas, petit */}
      {isMyPrep && session.status !== 'prete' && session.status !== 'depart_valide' && (
        <div style={{ textAlign: 'center', marginTop: 12, marginBottom: 16 }}>
          <button onClick={releasePrep} style={{
            padding: '8px 16px', borderRadius: 8,
            border: '1px solid #C0392B', background: '#FDECEA',
            color: '#C0392B', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Je libère cette pharmacie</button>
        </div>
      )}

      {/* Supprimer — responsables uniquement */}
      {user.role === 'responsable' && (
        <div style={{ textAlign: 'center', marginTop: 4, marginBottom: 16 }}>
          <button onClick={deleteSession} style={{
            padding: '6px 14px', borderRadius: 8,
            border: '1px dashed #C0392B44', background: 'transparent',
            color: '#C0392B', fontSize: 11, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit', opacity: 0.7,
          }}>🗑️ Supprimer cette pharmacie</button>
        </div>
      )}

      <QrScanner open={scannerOpen} onClose={() => setScannerOpen(false)}
        onScan={(code) => {
          try { setScannerOpen(false); if (!code) return; setNewCaisse(code); showToast('Code scanné : ' + code, 'success'); setOpenStep('caisses'); }
          catch (e) { console.error('Erreur scan:', e); setScannerOpen(false); showToast('Erreur scan — tape le code manuellement', 'error'); }
        }} />

      </>}
    </div>
  );
}

// ═══ STEP COMPONENTS — définis HORS de PrepDetail pour éviter les remontages ═══
function StepRow({ id, num, title, subtitle, count, done, warning, openStep, onToggle }) {
  const isOpen = openStep === id;
  const borderColor = isOpen ? '#1A5276' : done ? '#82E0AA' : '#E8E8EE';
  return (
    <div onClick={() => onToggle(id)} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
      background: 'white', cursor: 'pointer', userSelect: 'none',
      borderRadius: isOpen ? '10px 10px 0 0' : 10,
      border: '0.5px solid ' + borderColor,
    }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
        background: warning ? '#FDECEA' : done ? '#D5F5E3' : '#F2F3F7',
        color: warning ? '#C0392B' : done ? '#1E6B3C' : '#9CA3AF',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 600,
      }}>{warning ? '!' : done ? '✓' : num}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1D23' }}>{title}</div>
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>{subtitle}</div>
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: count > 0 ? (warning ? '#C0392B' : '#1E6B3C') : '#D1D5DB', flexShrink: 0 }}>
        {count !== null ? count : '—'}
      </span>
    </div>
  );
}

function StepContent({ id, openStep, children }) {
  if (openStep !== id) return null;
  return (
    <div style={{ padding: '12px', background: 'white', borderRadius: '0 0 10px 10px', border: '0.5px solid #1A5276', borderTop: 'none' }}>
      {children}
    </div>
  );
}
